import { faNewspaper } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Roles } from "../../../types/user.types";
import { useExternalDataService } from "../../../services/external-data.service";
import { useMatterParametersService } from "../../../services/matter-parameters.service";
import { CirculatorSearchDTO } from "../../../types/external-data.types";
import { InlineSpinner } from "../../../components/inline-spinner";
import { useAuthGuard } from "../../../hooks/useAuthGuard";
import HeaderNavBar from "../../../components/header-nav-bar";

interface FormValues {
  circulatorId: string;
  circulatorName: string;
}

export default function FreeFormCirculatorSearch() {
  useAuthGuard(Roles.Admin);

  const { freeFormCirculatorSearch } = useExternalDataService();
  const { getMatterParameters } = useMatterParametersService();
  const { matterId } = useParams();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [circulatorSearchResults, setCirculatorSearchResults] = useState<CirculatorSearchDTO[]>([]);
  const [totalHits, setTotalHits] = useState<number>(0);
  const [initiativeSerialNumber, setInitiativeSerialNumber] = useState<string>("");

  useEffect(() => {
    if (matterId) {
      getMatterParameters(matterId)
        .then((variables) => {
          const serialNumberVar = variables.find((v) => v.key === "InitiativeSerialNumber");
          if (serialNumberVar) {
            setInitiativeSerialNumber(serialNumberVar.value);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch matter variables:", error);
        });
    }
  }, [matterId, getMatterParameters]);

  const handleSave = async (formValues: FormValues) => {
    const { circulatorId, circulatorName } = formValues;

    setSuccessMessage("");
    setErrorMessage("");
    setLoading(true);
    if (matterId == null) {
      return;
    }

    const criteria = {
      circulatorId,
      circulatorName,
    };

    try {
      const result = await freeFormCirculatorSearch(matterId, criteria);
      
      // Get distinct circulators by circulatorId and circulatorName combination
      // Prioritize results that contain the InitiativeSerialNumber
      const distinctResults = result.results.reduce((acc: CirculatorSearchDTO[], current) => {
        const existingIndex = acc.findIndex(item => 
          item.circulatorId === current.circulatorId && 
          item.circulatorName === current.circulatorName
        );
        
        if (existingIndex === -1) {
          // No existing entry, add this one
          acc.push(current);
        } else {
          // Entry exists, replace it if current one has the InitiativeSerialNumber and existing doesn't
          const existing = acc[existingIndex];
          const currentHasSerial = current.candidateOrBallotMeasure?.includes(initiativeSerialNumber) || false;
          const existingHasSerial = existing.candidateOrBallotMeasure?.includes(initiativeSerialNumber) || false;
          
          if (currentHasSerial && !existingHasSerial) {
            acc[existingIndex] = current;
          }
        }
        return acc;
      }, []);
      
      setCirculatorSearchResults(distinctResults);
      setTotalHits(distinctResults.length);
    } catch (error) {
      setErrorMessage("Failed to search circulators. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <HeaderNavBar backTo="details" />
      <div className="row tablet-view justify-content-start">
        <div className="col-4 tablet-left d-flex flex-column">
          <div className="tablet-buffer d-flex flex-fill flex-column mb-2">
            <div className="mb-auto">
              <h2>Circulator Search</h2>
              {initiativeSerialNumber && (
                <div className="mt-2">
                  <strong>Initiative Serial Number:</strong> {initiativeSerialNumber}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-8 tablet-right">
          <div className="tablet-buffer">
            <Formik
              initialValues={{
                circulatorId: "",
                circulatorName: "",
              }}
              onSubmit={(formValues) => {
                handleSave(formValues);
              }}
            >
              <Form>
                <div className="row">
                  <div className="col-6 form-group mb-3">
                    <label htmlFor="circulatorId">Circulator ID</label>
                    <Field name="circulatorId" type="text" placeholder="Registration ID" className="form-control" />
                    <ErrorMessage name="circulatorId" component="div" className="alert alert-danger p-2" />
                  </div>

                  <div className="col-6 form-group mb-3">
                    <label htmlFor="circulatorName">Name</label>
                    <Field name="circulatorName" type="text" placeholder="Name" className="form-control" />
                    <ErrorMessage name="circulatorName" component="div" className="alert alert-danger p-2" />
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-4">
                    <div className="form-group mb-3">
                      <button type="submit" className="btn btn-sw" disabled={loading}>
                        {loading && <InlineSpinner />}
                        <FontAwesomeIcon icon={faNewspaper} className="me-2" />
                        Search
                      </button>
                    </div>
                  </div>
                  <div className="col-sm-8 pt-2 text-end">
                    {totalHits > 0 && (
                      <span className="text-muted">
                        Showing {circulatorSearchResults?.length} of {totalHits} possible matches
                      </span>
                    )}
                  </div>
                </div>
                {errorMessage && (
                  <div className="form-group mb-3">
                    <div className="alert alert-danger" role="alert">
                      {errorMessage}
                    </div>
                  </div>
                )}
                {successMessage && (
                  <div className="form-group mb-3">
                    <div className="alert alert-success" role="alert">
                      {successMessage}
                    </div>
                  </div>
                )}
              </Form>
            </Formik>
            <div
              style={{
                maxHeight: 400,
                overflowY: "auto",
                overflowX: "hidden",
                marginTop: "1.5 rem",
              }}
            >
              {circulatorSearchResults?.length > 0 && (
                <table className="table table-borderless">
                  <tbody>
                    {circulatorSearchResults?.map((circulatorSearchResult, index) => (
                      <tr key={index} className="mb-3 border">
                        <td className="card-body">
                          <div className="row">
                            <div className="col-md-12">
                              <p className="card-text mb-0">
                                <strong>{circulatorSearchResult?.circulatorName}</strong>
                                &nbsp;-- Registration ID: {circulatorSearchResult?.circulatorId || ""}
                              </p>
                            </div>
                          </div>
                          {circulatorSearchResult?.candidateOrBallotMeasure &&
                            circulatorSearchResult.candidateOrBallotMeasure.includes(initiativeSerialNumber) && (
                              <div className="row">
                                <div className="col-md-12">
                                  <p className="card-text mb-0">
                                    <strong>Candidate/Ballot Measure:</strong>{" "}
                                    {circulatorSearchResult.candidateOrBallotMeasure}
                                  </p>
                                </div>
                              </div>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
